import { ArrowLeftIcon, PaperClipIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { getConfig, getTicketDetails, sendTicketMessage } from 'services/apis/ticket'
import {
  Button,
  DocumentFile,
  InputFileTable,
  TextArea,
  Tooltip,
} from 'stories/components'
import { formatTime } from 'utils'
import { useTitle } from 'utils/pageTitle'

import { type IAsanaTicketItem, IAsanaAttachment, ITicketDetailsItem } from './types'

export const ExternalTicketItem = () => {
  const navigate = useHistory()

  const { id: ticketId } = useParams<{ id: string }>()

  const auth = useSelector((state: any) => state.auth)

  const [documentFiles, setDocumentFiles] = useState<DocumentFile[]>([])
  const [isLoading, setLoading] = useState(false)
  const [messages, setMessages] = useState<IAsanaTicketItem[]>([])
  const [attachments, setAttachments] = useState<IAsanaAttachment[]>([])
  const [msgText, setMsgText] = useState<string>('')
  const [details, setDetails] = useState<ITicketDetailsItem>()
  const [customKeys, setCustomKeys] = useState<string[]>([])
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({})

  useTitle(details?.name || 'Ticket Details')

  useEffect(() => {
    if (isLoading || !ticketId) return

    getMsgs(ticketId)
  }, [ticketId])

  useEffect(() => {
    getConfig().then((data: any) => {
      const tempKeys = Object.keys(data || {})
      setCustomKeys(tempKeys)
    })
  }, [])

  useEffect(() => {
    if (!details || customKeys.length === 0) return
    const customValues = {} as Record<string, string>
    customKeys.forEach((fieldKey) => {
      const customField = details?.custom_fields?.find((field: any) => field.name === fieldKey)
      customValues[fieldKey] = customField?.display_value || ''
    })
    setCustomFieldValues(customValues)
  }, [customKeys, details])

  const getMsgs = (ticketId: string) => {
    setLoading(true)
    getTicketDetails(ticketId)
      .then(({ stories, attachments, ticketDetails }) => {
        const filteredStories = stories.filter((story: any) =>
          ['attachment_added', 'comment_added'].includes(story.resource_subtype),
        )
        setMessages(filteredStories)
        setAttachments(attachments)
        setDetails(ticketDetails)
      })
      .finally(() => setLoading(false))
  }

  const sendTicketMsg = async () => {
    if (msgText.length > 0) {
      let msg: Record<string, any> = {
        ticketId: ticketId,
        text: `<p>${msgText}</p>`,
        createdBy: auth.profile.name ?? 'User',
        email: auth.profile.email,
        phone: auth.profile.phone,
        files: documentFiles,
      }

      setLoading(true)
      sendTicketMessage(msg)
        .then(() => {
          getMsgs(ticketId)
          setMsgText('')
          setDocumentFiles([])
        })
        .finally(() => setLoading(false))
    }
  }

  const handleTicketMsg = (msg: string) => {
    setMsgText(msg)
  }

  const goBackToTickets = () => {
    navigate.push('/tickets')
  }

  const handleFilesChange = (files: DocumentFile[]) => {
    setDocumentFiles(files)
  }

  return (
    <div className="py-6 px-2">
      <div className="relative shadow1 max-w-screen-2xl m-auto bg-white rounded p-3 md:p-7 pb-3 md:pb-3 sm:text-center lg:text-left w-full mb-6">
        <LayoutLoading show={isLoading} />

        <div className="flex flex-wrap items-center gap-2 mb-3 justify-between">
          <h1
            className="text-2xl font-variation-settings-600 flex gap-1 items-center cursor-pointer text-shade-blue hover:underline"
            onClick={goBackToTickets}
          >
            <ArrowLeftIcon className="w-3 h-3" />
            <span className="ml-2 uppercase text-sm">Back</span>
          </h1>
        </div>
        <div className="py-6 border-b">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Ticket Details</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1">Subject:</p>
              <p className="text-base text-gray-900">{details?.name}</p>
            </div>

            {customFieldValues &&
              Object.entries(customFieldValues as Record<string, string>).map(([key, value]) => (
                <div key={key}>
                  <p className="text-sm font-semibold text-gray-600 mb-1">{key}:</p>
                  <p className="text-base text-gray-900">{value || '-'}</p>
                </div>
              ))}
          </div>

          <div className="mt-4">
            <p className="text-sm font-semibold text-gray-600 mb-1">Description:</p>
            <div className="text-xs ck-content !min-h-fit mt-4 whitespace-pre-wrap">{details?.notes ?? ''}</div>
          </div>

          {attachments.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-semibold text-gray-600 mb-2">Attachments:</p>
              <div className="flex flex-wrap gap-2">
                {attachments.map((attachment, index) => (
                  <Tooltip key={index} message={`Download (${attachment.name})`} position="bottom">
                    <a
                      key={index}
                      href={attachment.download_url}
                      download={attachment.name}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-md text-sm text-gray-700 hover:text-gray-900 transition-colors cursor-pointer group"
                    >
                      <PaperClipIcon className="w-4 h-4 text-gray-500 group-hover:text-gray-700" />
                      <span className="truncate max-w-[200px]">{attachment.name}</span>
                    </a>
                  </Tooltip>
                ))}
              </div>
            </div>
          )}
        </div>

        {messages.slice().map((msg, index) => (
          <div key={index}>
            <div className="py-4 border-b px-8 border-l-2 border-l-gray-400 bg-gray-100">
              <div className="flex justify-between items-center">
                <p className="font-semibold">{msg.created_by.name}</p>
                <p className="font-light text-gray-500 text-sm">{formatTime(msg.created_at)}</p>
              </div>
              <div className="text-xs ck-content !min-h-fit mt-4 whitespace-pre-wrap">{msg.text ?? ''}</div>
              {msg?.downloaded_url && msg?.file_name && (
                <div className="mt-2">
                  <a
                    href={msg?.downloaded_url}
                    download={msg?.file_name}
                    className="text-shade-blue hover:underline text-sm flex items-center gap-1 cursor-pointer"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {msg?.file_name}
                  </a>
                </div>
              )}
            </div>
          </div>
        ))}
        <div className="w-full border-t-2 border-t-gray-200">
          <p className="my-4 text-sm text-gray-900">Message Content</p>
          <div className="">
            <TextArea value={msgText} onChange={(value) => handleTicketMsg(value)} required placeholder="Reply" />
            <div className="mt-2">
              <InputFileTable
                value={documentFiles}
                onChange={handleFilesChange}
                title="Files"
                acceptFileTypes="application/*,image/*"
                filePath={`nexys/ticket/${ticketId}`}
                showStatus={false}
                showCategory={false}
              />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4 pb-6">
            <Button onClick={sendTicketMsg} className="h-fit" size="sm">
              Submit
            </Button>
            <Button
              onClick={() => {
                goBackToTickets()
              }}
              className="h-fit"
              outline
              size="sm"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
